type Issue = { severity: 'error' | 'warning' | 'info'; message: string };
type DnsRecord = { type: string; name: string; value: string; priority?: number | null; ttl: number };
type ParsedHeader = { name: string; value: string };
type MimePart = { contentType: string; encoding: string; disposition: string; size: number; preview: string };
type SmtpStep = { direction: string; message: string; elapsedMilliseconds: number };
type SmtpResponse = { steps: SmtpStep[]; issues: Issue[]; success: boolean };
type ToolKeyValue = { label: string; value: string };
type BlacklistResult = { listName: string; query: string; listed: boolean; responses: string[]; status: string };

const $ = <T extends Element>(selector: string, root: ParentNode = document) => root.querySelector<T>(selector);
const $$ = <T extends Element>(selector: string, root: ParentNode = document) => Array.from(root.querySelectorAll<T>(selector));

function formValues(form: HTMLFormElement): Record<string, string> {
  return Object.fromEntries(new FormData(form).entries()) as Record<string, string>;
}

function lines(value: string | undefined): string[] {
  return (value ?? '').split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Något gick fel.' }));
    throw new Error(error.message ?? 'Något gick fel.');
  }
  return response.json() as Promise<T>;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character] ?? character);
}

function codeBlock(label: string, value: string, downloadName?: string): string {
  const download = downloadName ? ` data-download-name="${escapeHtml(downloadName)}"` : '';
  return `<div class="tool-code-block"><div><strong>${label}</strong><button type="button" data-copy>Copy</button>${downloadName ? '<button type="button" data-download>Download</button>' : ''}</div><pre${download}>${escapeHtml(value)}</pre></div>`;
}

function renderIssues(issues: Issue[] = []): string {
  if (issues.length === 0) {
    return '<div class="tool-issue tool-issue--info">Inga problem hittades.</div>';
  }
  return issues.map((issue) => `<div class="tool-issue tool-issue--${issue.severity}">${escapeHtml(issue.message)}</div>`).join('');
}

function renderRecords(records: DnsRecord[]): string {
  if (records.length === 0) {
    return '<p class="tool-empty">Inga poster att visa.</p>';
  }
  return `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Typ</th><th>Namn</th><th>Prioritet</th><th>Värde</th><th>TTL</th></tr></thead><tbody>${records.map((record) => `<tr><td>${record.type}</td><td>${escapeHtml(record.name)}</td><td>${record.priority ?? ''}</td><td><code>${escapeHtml(record.value)}</code></td><td>${record.ttl}s</td></tr>`).join('')}</tbody></table></div>`;
}

function setLoading(result: Element, message = 'Arbetar...') {
  result.innerHTML = `<div class="tool-loading">${message}</div>`;
}

function setError(result: Element, error: unknown) {
  result.innerHTML = `<div class="tool-issue tool-issue--error">${escapeHtml(error instanceof Error ? error.message : 'Något gick fel.')}</div>`;
}

async function handleSpf(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const providers = $$<HTMLInputElement>('input[name="provider"]:checked', form).map((input) => input.value);
  const response = await api<{ record: string; characterLength: number; dnsLookupCount: number; issues: Issue[] }>('/api/verktyg/spf/generera', {
    method: 'POST',
    body: JSON.stringify({
      domain: values.domain,
      includes: [...providers, ...lines(values.includes)],
      ip4: lines(values.ip4),
      ip6: lines(values.ip6),
      allMechanism: values.allMechanism
    })
  });
  result.innerHTML = `${renderIssues(response.issues)}${codeBlock('TXT-post', response.record)}<div class="tool-metrics"><span>${response.characterLength} tecken</span><span>${response.dnsLookupCount}/10 DNS-uppslag</span></div>`;
}

async function handleDkim(form: HTMLFormElement, result: Element, submitter?: HTMLElement | null) {
  const values = formValues(form);
  if (submitter?.getAttribute('value') === 'validate') {
    const response = await api<{ hostName: string; records: DnsRecord[]; issues: Issue[] }>(`/api/verktyg/dkim/validera?domain=${encodeURIComponent(values.domain)}&selector=${encodeURIComponent(values.selector)}`);
    result.innerHTML = `${renderIssues(response.issues)}<h2>${escapeHtml(response.hostName)}</h2>${renderRecords(response.records)}`;
    return;
  }
  const response = await api<{ hostName: string; txtRecord: string; publicKeyPem: string; privateKeyPem: string; issues: Issue[] }>('/api/verktyg/dkim/generera', {
    method: 'POST',
    body: JSON.stringify({ domain: values.domain, selector: values.selector, keySize: Number(values.keySize) })
  });
  result.innerHTML = `${renderIssues(response.issues)}<h2>${escapeHtml(response.hostName)}</h2>${codeBlock('DNS TXT', response.txtRecord)}${codeBlock('Publik nyckel', response.publicKeyPem, 'dkim-public.pem')}${codeBlock('Privat nyckel', response.privateKeyPem, 'dkim-private.pem')}`;
}

async function handleDmarc(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ hostName: string; record?: string | null; tags: { tag: string; value: string; meaning: string }[]; issues: Issue[]; recommendations: string[] }>(`/api/verktyg/dmarc/kontrollera?domain=${encodeURIComponent(values.domain)}`);
  const tags = response.tags.map((tag) => `<tr><td><code>${escapeHtml(tag.tag)}</code></td><td>${escapeHtml(tag.value)}</td><td>${escapeHtml(tag.meaning)}</td></tr>`).join('');
  result.innerHTML = `${renderIssues(response.issues)}<h2>${escapeHtml(response.hostName)}</h2>${response.record ? codeBlock('DMARC TXT', response.record) : ''}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Tagg</th><th>Värde</th><th>Förklaring</th></tr></thead><tbody>${tags}</tbody></table></div>${renderRecommendations(response.recommendations)}`;
}

async function handleMx(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ records: { host: string; priority: number; ipAddresses: string[]; provider: string; ttl: number }[]; issues: Issue[] }>(`/api/verktyg/mx/uppslagning?domain=${encodeURIComponent(values.domain)}`);
  const rows = response.records.map((record) => `<tr><td>${record.priority}</td><td>${escapeHtml(record.host)}</td><td>${record.ipAddresses.map(escapeHtml).join('<br>')}</td><td>${escapeHtml(record.provider)}</td><td>${record.ttl}s</td></tr>`).join('');
  result.innerHTML = `${renderIssues(response.issues)}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Prioritet</th><th>Host</th><th>IP</th><th>Leverantör</th><th>TTL</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

async function handleDns(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(values.domain)}&type=${encodeURIComponent(values.type)}`);
  result.innerHTML = `${renderIssues(response.issues)}${response.fromCache ? '<p class="tool-cache">Svar från cache.</p>' : ''}${renderRecords(response.records)}`;
}

async function handleDeliverability(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ checks: { name: string; status: string; details: string }[]; headers: { name: string; value: string }[]; recommendations: string[]; spamAssassinApproximation: number }>('/api/verktyg/leveransbarhet/analysera', {
    method: 'POST',
    body: JSON.stringify(values)
  });
  const checks = response.checks.map((check) => `<div class="tool-check-result tool-check-result--${check.status}"><strong>${escapeHtml(check.name)}</strong><span>${escapeHtml(check.details)}</span></div>`).join('');
  const headers = response.headers.slice(0, 30).map((header) => `<tr><td>${escapeHtml(header.name)}</td><td>${escapeHtml(header.value)}</td></tr>`).join('');
  result.innerHTML = `<div class="tool-score">SpamAssassin ungefärlig score: <strong>${response.spamAssassinApproximation}</strong></div><div class="tool-check-grid">${checks}</div>${renderRecommendations(response.recommendations)}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Header</th><th>Värde</th></tr></thead><tbody>${headers}</tbody></table></div>`;
}

async function handleSmtp(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<SmtpResponse>('/api/verktyg/smtp/testa', {
    method: 'POST',
    body: JSON.stringify({
      host: values.host,
      port: Number(values.port),
      useStartTls: Boolean(values.useStartTls),
      useSsl: Boolean(values.useSsl),
      username: values.username,
      password: values.password,
      mailFrom: values.mailFrom,
      rcptTo: values.rcptTo,
      sendMessage: Boolean(values.sendMessage)
    })
  });
  const log = response.steps.map((step) => `<div class="tool-smtp-line"><span>${step.elapsedMilliseconds} ms</span><strong>${escapeHtml(step.direction)}</strong><code>${escapeHtml(step.message)}</code></div>`).join('');
  result.innerHTML = `${renderIssues(response.issues)}<div class="tool-issue tool-issue--${response.success ? 'info' : 'warning'}">${response.success ? 'SMTP-testet slutfördes.' : 'SMTP-testet slutfördes inte.'}</div><div class="tool-smtp-log">${log}</div>`;
}

async function runSmtpProbe(values: Record<string, string>, options: Partial<{ useStartTls: boolean; useSsl: boolean }> = {}): Promise<SmtpResponse> {
  return api<SmtpResponse>('/api/verktyg/smtp/testa', {
    method: 'POST',
    body: JSON.stringify({
      host: values.host,
      port: Number(values.port),
      useStartTls: options.useStartTls ?? Boolean(values.useStartTls),
      useSsl: options.useSsl ?? Boolean(values.useSsl),
      username: '',
      password: '',
      mailFrom: '',
      rcptTo: '',
      sendMessage: false
    })
  });
}

function renderSmtpLog(steps: SmtpStep[]): string {
  if (steps.length === 0) {
    return '<p class="tool-empty">Ingen SMTP-logg att visa.</p>';
  }
  return `<div class="tool-smtp-log">${steps.map((step) => `<div class="tool-smtp-line"><span>${step.elapsedMilliseconds} ms</span><strong>${escapeHtml(step.direction)}</strong><code>${escapeHtml(step.message)}</code></div>`).join('')}</div>`;
}

function smtpServerText(steps: SmtpStep[]): string {
  return steps.filter((step) => step.direction === 'server').map((step) => step.message).join('\n');
}

function renderRecommendations(recommendations: string[]): string {
  if (recommendations.length === 0) {
    return '';
  }
  return `<div class="tool-recommendations"><strong>Rekommendationer</strong><ul>${recommendations.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul></div>`;
}

function renderKeyValues(items: { label: string; value: string }[]): string {
  if (items.length === 0) {
    return '<p class="tool-empty">Inga värden att visa.</p>';
  }
  return `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Fält</th><th>Värde</th></tr></thead><tbody>${items.map((item) => `<tr><td>${escapeHtml(item.label)}</td><td>${escapeHtml(item.value)}</td></tr>`).join('')}</tbody></table></div>`;
}

function parseHeaders(raw: string): ParsedHeader[] {
  const headerText = raw.replace(/\r\n/g, '\n').split(/\n\n/, 1)[0] ?? '';
  const unfolded = headerText.replace(/\n[ \t]+/g, ' ');
  return unfolded
    .split('\n')
    .map((line) => {
      const index = line.indexOf(':');
      if (index < 1) {
        return null;
      }
      return { name: line.slice(0, index).trim(), value: line.slice(index + 1).trim() };
    })
    .filter((item): item is ParsedHeader => item !== null);
}

function headerValues(headers: ParsedHeader[], name: string): string[] {
  return headers.filter((header) => header.name.toLowerCase() === name.toLowerCase()).map((header) => header.value);
}

function extractBoundary(contentType: string): string | null {
  const match = contentType.match(/boundary=(?:"([^"]+)"|([^;\s]+))/i);
  return match?.[1] ?? match?.[2] ?? null;
}

function splitHeaderAndBody(raw: string): { headers: ParsedHeader[]; body: string } {
  const normalized = raw.replace(/\r\n/g, '\n');
  const index = normalized.indexOf('\n\n');
  if (index === -1) {
    return { headers: parseHeaders(normalized), body: '' };
  }
  return { headers: parseHeaders(normalized.slice(0, index)), body: normalized.slice(index + 2) };
}

function decodeQuotedPrintable(value: string): string {
  const bytes = value
    .replace(/=\n/g, '')
    .replace(/=([0-9A-F]{2})/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
  try {
    return decodeURIComponent(escape(bytes));
  } catch {
    return bytes;
  }
}

function decodeBase64Text(value: string): string {
  const binary = atob(value.replace(/\s+/g, ''));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function encodeBase64Text(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function decodeMimeBody(body: string, encoding: string): string {
  if (encoding.toLowerCase() === 'base64') {
    return decodeBase64Text(body);
  }
  if (encoding.toLowerCase() === 'quoted-printable') {
    return decodeQuotedPrintable(body);
  }
  return body;
}

function collectMimeParts(raw: string, parts: MimePart[] = [], depth = 0): MimePart[] {
  if (depth > 4) {
    return parts;
  }
  const { headers, body } = splitHeaderAndBody(raw);
  const contentType = headerValues(headers, 'Content-Type')[0] ?? 'text/plain';
  const encoding = headerValues(headers, 'Content-Transfer-Encoding')[0] ?? '7bit';
  const disposition = headerValues(headers, 'Content-Disposition')[0] ?? '';
  const boundary = extractBoundary(contentType);

  if (boundary) {
    body.split(`--${boundary}`)
      .map((part) => part.trim())
      .filter((part) => part && part !== '--')
      .forEach((part) => collectMimeParts(part.replace(/--$/, '').trim(), parts, depth + 1));
    return parts;
  }

  let preview = body.trim();
  try {
    preview = decodeMimeBody(preview, encoding).trim();
  } catch {
    preview = body.trim();
  }
  parts.push({
    contentType,
    encoding,
    disposition,
    size: body.length,
    preview: preview.slice(0, 500)
  });
  return parts;
}

function spfTerms(record: string): { label: string; value: string }[] {
  return record
    .split(/\s+/)
    .filter((term) => term && term.toLowerCase() !== 'v=spf1')
    .map((term) => {
      if (term.startsWith('include:')) {
        return { label: 'Include', value: term.slice('include:'.length) };
      }
      if (term.startsWith('ip4:')) {
        return { label: 'IPv4', value: term.slice('ip4:'.length) };
      }
      if (term.startsWith('ip6:')) {
        return { label: 'IPv6', value: term.slice('ip6:'.length) };
      }
      if (/^[~+\-?]all$/i.test(term)) {
        return { label: 'Policy', value: term };
      }
      if (term.startsWith('mx')) {
        return { label: 'MX-mekanism', value: term };
      }
      if (term.startsWith('a')) {
        return { label: 'A-mekanism', value: term };
      }
      return { label: 'Mekanism', value: term };
    });
}

function ipv4PtrName(ipAddress: string): string | null {
  const octets = ipAddress.trim().split('.');
  if (octets.length !== 4 || octets.some((octet) => !/^\d+$/.test(octet) || Number(octet) > 255)) {
    return null;
  }
  return `${octets.reverse().join('.')}.in-addr.arpa`;
}

function expandIpv6(ipAddress: string): string | null {
  const value = ipAddress.trim().toLowerCase();
  if (!/^[0-9a-f:]+$/i.test(value) || value.split('::').length > 2) {
    return null;
  }
  const [left, right = ''] = value.split('::');
  const leftGroups = left ? left.split(':') : [];
  const rightGroups = right ? right.split(':') : [];
  if ([...leftGroups, ...rightGroups].some((group) => !/^[0-9a-f]{1,4}$/i.test(group))) {
    return null;
  }
  const missing = 8 - leftGroups.length - rightGroups.length;
  if (missing < 0 || (!value.includes('::') && missing !== 0)) {
    return null;
  }
  return [...leftGroups, ...Array<string>(missing).fill('0'), ...rightGroups]
    .map((group) => group.padStart(4, '0'))
    .join('');
}

function ipv6PtrName(ipAddress: string): string | null {
  const expanded = expandIpv6(ipAddress);
  if (!expanded) {
    return null;
  }
  return `${expanded.split('').reverse().join('.')}.ip6.arpa`;
}

async function handleSpfLookup(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(values.domain)}&type=TXT`);
  const spfRecords = response.records.filter((record) => record.value.toLowerCase().startsWith('v=spf1'));
  if (spfRecords.length === 0) {
    result.innerHTML = `${renderIssues([...response.issues, { severity: 'warning', message: 'Ingen SPF-post hittades bland TXT-posterna.' }])}${renderRecords(response.records)}`;
    return;
  }
  const issues: Issue[] = [];
  if (spfRecords.length > 1) {
    issues.push({ severity: 'error', message: 'Domänen har fler än en SPF-post. Mottagare kan tolka SPF som ogiltigt.' });
  }
  const record = spfRecords[0].value;
  const lookupTerms = (record.match(/\b(include|a|mx|exists|redirect)[:=\w.-]*/gi) ?? []).length;
  if (lookupTerms > 10) {
    issues.push({ severity: 'error', message: 'SPF-posten verkar kunna passera gränsen på 10 DNS-uppslag.' });
  } else if (lookupTerms > 7) {
    issues.push({ severity: 'warning', message: 'SPF-posten ligger nära gränsen på 10 DNS-uppslag.' });
  }
  result.innerHTML = `${renderIssues(issues)}${codeBlock('SPF TXT', record)}${renderKeyValues(spfTerms(record))}<div class="tool-metrics"><span>${record.length} tecken</span><span>${lookupTerms}/10 ungefärliga DNS-uppslag</span></div>`;
}

async function handleTxtLookup(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(values.domain)}&type=TXT`);
  result.innerHTML = `${renderIssues(response.issues)}${response.fromCache ? '<p class="tool-cache">Svar från cache.</p>' : ''}${renderRecords(response.records)}`;
}

async function handleReverseDns(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const ptrName = ipv4PtrName(values.ipAddress) ?? ipv6PtrName(values.ipAddress);
  if (!ptrName) {
    throw new Error('Skriv en giltig IPv4- eller IPv6-adress.');
  }
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(ptrName)}&type=PTR`);
  result.innerHTML = `${renderIssues(response.issues)}${codeBlock('PTR-namn', ptrName)}${renderRecords(response.records)}`;
}

async function handleHeaderAnalyzer(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const headers = parseHeaders(values.headers);
  const received = headerValues(headers, 'Received');
  const auth = headerValues(headers, 'Authentication-Results');
  const summary = [
    { label: 'From', value: headerValues(headers, 'From')[0] ?? '-' },
    { label: 'To', value: headerValues(headers, 'To')[0] ?? '-' },
    { label: 'Subject', value: headerValues(headers, 'Subject')[0] ?? '-' },
    { label: 'Date', value: headerValues(headers, 'Date')[0] ?? '-' },
    { label: 'Message-ID', value: headerValues(headers, 'Message-ID')[0] ?? '-' },
    { label: 'Received-steg', value: String(received.length) }
  ];
  const authRows = auth.length === 0
    ? '<p class="tool-empty">Inga Authentication-Results hittades.</p>'
    : auth.map((item) => `<div class="tool-issue tool-issue--info">${escapeHtml(item)}</div>`).join('');
  const receivedRows = received.map((item, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(item)}</td></tr>`).join('');
  result.innerHTML = `${renderKeyValues(summary)}<h2>Autentisering</h2>${authRows}<h2>Received-kedja</h2><div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>#</th><th>Header</th></tr></thead><tbody>${receivedRows}</tbody></table></div>`;
}

async function handleMimeDecoder(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const { headers } = splitHeaderAndBody(values.message);
  const parts = collectMimeParts(values.message);
  const headerSummary = [
    { label: 'Content-Type', value: headerValues(headers, 'Content-Type')[0] ?? '-' },
    { label: 'MIME-Version', value: headerValues(headers, 'MIME-Version')[0] ?? '-' },
    { label: 'Subject', value: headerValues(headers, 'Subject')[0] ?? '-' },
    { label: 'Delar', value: String(parts.length) }
  ];
  const rows = parts.map((part, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(part.contentType)}</td><td>${escapeHtml(part.encoding)}</td><td>${escapeHtml(part.disposition || '-')}</td><td>${part.size}</td></tr>`).join('');
  const previews = parts.map((part, index) => codeBlock(`Förhandsvisning del ${index + 1}`, part.preview || '(tom)')).join('');
  result.innerHTML = `${renderKeyValues(headerSummary)}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>#</th><th>Content-Type</th><th>Kodning</th><th>Disposition</th><th>Storlek</th></tr></thead><tbody>${rows}</tbody></table></div>${previews}`;
}

async function handleBase64Codec(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const output = values.mode === 'decode' ? decodeBase64Text(values.value) : encodeBase64Text(values.value);
  result.innerHTML = codeBlock(values.mode === 'decode' ? 'Avkodad text' : 'Base64', output);
}

async function handleUrlCodec(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const output = values.mode === 'decode' ? decodeURIComponent(values.value.replace(/\+/g, ' ')) : encodeURIComponent(values.value);
  result.innerHTML = codeBlock(values.mode === 'decode' ? 'Avkodad URL-text' : 'URL-kodad text', output);
}

async function handleHtmlEntityCodec(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const output = values.mode === 'decode'
    ? new DOMParser().parseFromString(values.value, 'text/html').documentElement.textContent ?? ''
    : values.value.replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character] ?? character);
  result.innerHTML = codeBlock(values.mode === 'decode' ? 'Avkodad text' : 'HTML-entiteter', output);
}

function normalizeHost(value: string): string {
  return value.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').replace(/\.$/, '').toLowerCase();
}

function parseTagRecord(record: string): Record<string, string> {
  return Object.fromEntries(record.split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const index = part.indexOf('=');
      return index === -1 ? [part, ''] : [part.slice(0, index).trim(), part.slice(index + 1).trim()];
    }));
}

function spfLookupTerms(record: string): { mechanism: string; target: string; cost: number }[] {
  return record.split(/\s+/)
    .filter(Boolean)
    .map((term) => term.replace(/^[+~?-]/, ''))
    .filter((term) => /^(include:|a(?::|$)|mx(?::|$)|ptr(?::|$)|exists:|redirect=)/i.test(term))
    .map((term) => {
      const [mechanism, target = ''] = term.includes('=') ? term.split('=', 2) : term.split(':', 2);
      return { mechanism, target, cost: 1 };
    });
}

function validateSpfRecord(record: string): { issues: Issue[]; terms: { label: string; value: string }[]; lookupCount: number } {
  const normalized = record.trim().replace(/\s+/g, ' ');
  const issues: Issue[] = [];
  if (!normalized.toLowerCase().startsWith('v=spf1')) {
    issues.push({ severity: 'error', message: 'SPF-posten måste börja med v=spf1.' });
  }
  const terms = normalized.split(/\s+/).filter(Boolean);
  const allTerms = terms.filter((term) => /^[+~?-]?all$/i.test(term));
  if (allTerms.length === 0) {
    issues.push({ severity: 'warning', message: 'SPF-posten saknar avslutande all-mekanism.' });
  }
  if (allTerms.length > 1) {
    issues.push({ severity: 'error', message: 'SPF-posten har fler än en all-mekanism.' });
  }
  const allIndex = terms.findIndex((term) => /^[+~?-]?all$/i.test(term));
  if (allIndex !== -1 && allIndex !== terms.length - 1) {
    issues.push({ severity: 'warning', message: 'Mekanismer efter all kommer normalt inte att användas.' });
  }

  const mechanismPattern = /^[+~?-]?(all|include:[A-Za-z0-9._-]+|a(?::[A-Za-z0-9._-]+)?(?:\/\d{1,2})?|mx(?::[A-Za-z0-9._-]+)?(?:\/\d{1,2})?|ip4:(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?|ip6:[0-9A-Fa-f:]+(?:\/\d{1,3})?|exists:[A-Za-z0-9._%-]+|ptr(?::[A-Za-z0-9._-]+)?|redirect=[A-Za-z0-9._-]+|exp=[A-Za-z0-9._-]+)$/;
  terms.slice(1).forEach((term) => {
    if (!mechanismPattern.test(term)) {
      issues.push({ severity: 'warning', message: `Okänd eller ovanlig SPF-term: ${term}` });
    }
  });

  const lookupCount = spfLookupTerms(normalized).reduce((sum, term) => sum + term.cost, 0);
  if (lookupCount > 10) {
    issues.push({ severity: 'error', message: 'SPF-posten överskrider gränsen på 10 DNS-uppslag.' });
  } else if (lookupCount > 7) {
    issues.push({ severity: 'warning', message: 'SPF-posten ligger nära gränsen på 10 DNS-uppslag.' });
  }

  return { issues, terms: spfTerms(normalized), lookupCount };
}

async function lookupTxt(domain: string): Promise<DnsRecord[]> {
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(normalizeHost(domain))}&type=TXT`);
  return response.records;
}

async function lookupSpfRecord(domain: string): Promise<string> {
  const records = await lookupTxt(domain);
  const spfRecords = records.filter((record) => record.value.toLowerCase().startsWith('v=spf1'));
  if (spfRecords.length === 0) {
    throw new Error('Ingen SPF-post hittades för domänen.');
  }
  if (spfRecords.length > 1) {
    throw new Error('Fler än en SPF-post hittades. Rätta det innan posten flattenas.');
  }
  return spfRecords[0].value;
}

async function flattenSpfDomain(domain: string, maxDepth: number, seen = new Set<string>()): Promise<{ terms: string[]; trace: string[] }> {
  const normalizedDomain = normalizeHost(domain);
  if (seen.has(normalizedDomain)) {
    return { terms: [], trace: [`Cirkulär SPF-referens hoppades över: ${normalizedDomain}`] };
  }
  seen.add(normalizedDomain);

  const record = await lookupSpfRecord(normalizedDomain);
  const trace = [`${normalizedDomain}: ${record}`];
  const terms: string[] = [];

  for (const term of record.split(/\s+/).filter(Boolean).slice(1)) {
    const bareTerm = term.replace(/^[+~?-]/, '');
    if (bareTerm.startsWith('include:') && maxDepth > 0) {
      const child = await flattenSpfDomain(bareTerm.slice('include:'.length), maxDepth - 1, seen);
      terms.push(...child.terms.filter((childTerm) => !/^[+~?-]?all$/i.test(childTerm)));
      trace.push(...child.trace);
    } else if (bareTerm.startsWith('redirect=') && maxDepth > 0) {
      const child = await flattenSpfDomain(bareTerm.slice('redirect='.length), maxDepth - 1, seen);
      terms.push(...child.terms.filter((childTerm) => !/^[+~?-]?all$/i.test(childTerm)));
      trace.push(...child.trace);
    } else {
      terms.push(term);
    }
  }

  return { terms, trace };
}

function mailtoList(value: string): string[] {
  return lines(value).map((item) => item.includes(':') ? item : `mailto:${item}`);
}

async function handleSpfFlattener(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const flattened = await flattenSpfDomain(values.domain, Math.max(1, Math.min(5, Number(values.maxDepth) || 3)));
  const allTerm = flattened.terms.find((term) => /^[+~?-]?all$/i.test(term)) ?? '~all';
  const uniqueTerms = Array.from(new Set(flattened.terms.filter((term) => !/^[+~?-]?all$/i.test(term))));
  const record = `v=spf1 ${uniqueTerms.join(' ')} ${allTerm}`.replace(/\s+/g, ' ').trim();
  const validation = validateSpfRecord(record);
  result.innerHTML = `${renderIssues(validation.issues)}${codeBlock('Flattad SPF-post', record)}${renderKeyValues([
    { label: 'Termer', value: String(uniqueTerms.length) },
    { label: 'Ungefärliga DNS-uppslag efter flatten', value: String(validation.lookupCount) }
  ])}${codeBlock('Uppslagskedja', flattened.trace.join('\n'))}`;
}

async function handleSpfSyntax(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const validation = validateSpfRecord(values.record);
  result.innerHTML = `${renderIssues(validation.issues)}${renderKeyValues(validation.terms)}<div class="tool-metrics"><span>${values.record.trim().length} tecken</span><span>${validation.lookupCount}/10 DNS-uppslag</span></div>`;
}

async function handleSpfCounter(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const record = values.record?.trim() || await lookupSpfRecord(values.domain);
  const validation = validateSpfRecord(record);
  const rows = spfLookupTerms(record).map((term) => ({ label: term.mechanism, value: term.target || '(implicit)' }));
  result.innerHTML = `${renderIssues(validation.issues)}${codeBlock('SPF-post', record)}${renderKeyValues(rows)}<div class="tool-metrics"><span>${validation.lookupCount}/10 DNS-uppslag</span></div>`;
}

async function handleDmarcGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const tags = ['v=DMARC1', `p=${values.policy}`];
  const rua = mailtoList(values.rua);
  const ruf = mailtoList(values.ruf);
  if (rua.length > 0) {
    tags.push(`rua=${rua.join(',')}`);
  }
  if (ruf.length > 0) {
    tags.push(`ruf=${ruf.join(',')}`);
  }
  tags.push(`pct=${Math.max(1, Math.min(100, Number(values.pct) || 100))}`);
  if (values.alignment === 'strict') {
    tags.push('adkim=s', 'aspf=s');
  }
  const host = `_dmarc.${normalizeHost(values.domain)}`;
  result.innerHTML = `${renderIssues([])}<h2>${escapeHtml(host)}</h2>${codeBlock('DMARC TXT', tags.join('; '))}`;
}

async function handleBimiGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const issues: Issue[] = [];
  if (!/^https:\/\//i.test(values.logoUrl)) {
    issues.push({ severity: 'warning', message: 'BIMI-logotypen bör ligga på en publik HTTPS-URL.' });
  }
  const host = `${normalizeHost(values.selector)}._bimi.${normalizeHost(values.domain)}`;
  const record = `v=BIMI1; l=${values.logoUrl.trim()}${values.certificateUrl?.trim() ? `; a=${values.certificateUrl.trim()}` : ''}`;
  result.innerHTML = `${renderIssues(issues)}<h2>${escapeHtml(host)}</h2>${codeBlock('BIMI TXT', record)}`;
}

async function handleBimiChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const host = `${normalizeHost(values.selector)}._bimi.${normalizeHost(values.domain)}`;
  const records = await lookupTxt(host);
  const bimiRecords = records.filter((record) => record.value.toLowerCase().startsWith('v=bimi1'));
  if (bimiRecords.length === 0) {
    result.innerHTML = `${renderIssues([{ severity: 'warning', message: 'Ingen BIMI-post hittades.' }])}${renderRecords(records)}`;
    return;
  }
  const record = bimiRecords[0].value;
  const tags = parseTagRecord(record);
  const issues: Issue[] = [];
  if (!tags.l) {
    issues.push({ severity: 'error', message: 'BIMI-posten saknar l= med logo-URL.' });
  } else if (!/^https:\/\//i.test(tags.l)) {
    issues.push({ severity: 'warning', message: 'Logo-URL bör använda HTTPS.' });
  }
  result.innerHTML = `${renderIssues(issues)}<h2>${escapeHtml(host)}</h2>${codeBlock('BIMI TXT', record)}${renderKeyValues(Object.entries(tags).map(([label, value]) => ({ label, value })))}`;
}

async function handleMtaStsGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const domain = normalizeHost(values.domain);
  const id = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const dnsHost = `_mta-sts.${domain}`;
  const dnsRecord = `v=STSv1; id=${id}`;
  const policy = [
    'version: STSv1',
    `mode: ${values.mode}`,
    ...lines(values.mx).map((mxHost) => `mx: ${mxHost}`),
    `max_age: ${Math.max(86400, Number(values.maxAge) || 604800)}`
  ].join('\n');
  result.innerHTML = `${renderIssues([])}<h2>${escapeHtml(dnsHost)}</h2>${codeBlock('MTA-STS DNS TXT', dnsRecord)}${codeBlock('mta-sts.txt', policy, 'mta-sts.txt')}`;
}

async function handleTlsRptGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const host = `_smtp._tls.${normalizeHost(values.domain)}`;
  const record = `v=TLSRPTv1; rua=${mailtoList(values.rua).join(',')}`;
  result.innerHTML = `${renderIssues([])}<h2>${escapeHtml(host)}</h2>${codeBlock('TLS-RPT TXT', record)}`;
}

function smtpCodeMeaning(code: string): string {
  const meanings: Record<string, string> = {
    '211': 'Systemstatus eller hjälpsvar.',
    '214': 'Hjälpmeddelande.',
    '220': 'Servern är redo.',
    '221': 'Servern stänger anslutningen.',
    '235': 'Autentisering lyckades.',
    '250': 'Kommandot accepterades.',
    '251': 'Mottagaren är inte lokal men servern vidarebefordrar.',
    '354': 'Servern väntar på meddelandedata.',
    '421': 'Tjänsten är tillfälligt otillgänglig.',
    '450': 'Mailbox är tillfälligt otillgänglig.',
    '451': 'Lokalt serverfel. Försök igen senare.',
    '452': 'Otillräckligt lagringsutrymme eller temporär resursbrist.',
    '500': 'Syntaxfel eller okänt kommando.',
    '501': 'Syntaxfel i parameter.',
    '502': 'Kommandot stöds inte.',
    '503': 'Fel kommandoordning.',
    '530': 'Autentisering krävs.',
    '535': 'Autentisering misslyckades.',
    '550': 'Mailbox eller policy avvisar meddelandet.',
    '551': 'Mottagaren är inte lokal.',
    '552': 'Lagringsgräns eller storleksgräns överskriden.',
    '553': 'Mailboxnamn eller adress är ogiltig.',
    '554': 'Transaktionen misslyckades.'
  };
  return meanings[code] ?? 'Okänd eller leverantörsspecifik SMTP-kod.';
}

function smtpClass(code: string): string {
  if (code.startsWith('2')) {
    return 'Lyckat svar';
  }
  if (code.startsWith('3')) {
    return 'Fortsättningssvar';
  }
  if (code.startsWith('4')) {
    return 'Tillfälligt fel';
  }
  if (code.startsWith('5')) {
    return 'Permanent fel';
  }
  return 'Okänd klass';
}

async function handleSmtpResponseDecoder(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const rows = lines(values.responses).map((line) => {
    const match = line.match(/^(\d{3})(?:[ -](\d\.\d\.\d))?\s*(.*)$/);
    if (!match) {
      return `<tr><td>-</td><td>-</td><td>${escapeHtml(line)}</td><td>Kunde inte tolka raden.</td></tr>`;
    }
    const [, code, enhanced = '-', message = ''] = match;
    return `<tr><td>${code}</td><td>${enhanced}</td><td>${escapeHtml(smtpClass(code))}</td><td>${escapeHtml(smtpCodeMeaning(code))}${message ? `<br><small>${escapeHtml(message)}</small>` : ''}</td></tr>`;
  }).join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Kod</th><th>Enhanced</th><th>Klass</th><th>Tolkning</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

async function handleSubjectLength(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const subject = values.subject.trim();
  const issues: Issue[] = [];
  if (subject.length > 70) {
    issues.push({ severity: 'warning', message: 'Ämnesraden är lång och kan klippas i många inkorgar.' });
  } else if (subject.length < 20) {
    issues.push({ severity: 'info', message: 'Kort ämnesrad. Kontrollera att den fortfarande är tydlig.' });
  }
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([
    { label: 'Tecken', value: String(subject.length) },
    { label: 'Ord', value: String(lines(subject.replace(/\s+/g, ',')).length) },
    { label: 'UTF-8 bytes', value: String(utf8ByteLength(subject)) },
    { label: 'Mobil förhandsvisning', value: subject.slice(0, 45) }
  ])}`;
}

async function handlePreheaderLength(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const preheader = values.preheader.trim();
  const issues: Issue[] = [];
  if (preheader.length > 100) {
    issues.push({ severity: 'warning', message: 'Preheadern är lång och kommer ofta att klippas.' });
  }
  if (preheader.length < 35) {
    issues.push({ severity: 'info', message: 'Preheadern är kort. Det kan lämna plats för oönskad malltext i inkorgen.' });
  }
  const snippet = `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${escapeHtml(preheader)}&#847;&zwnj;&nbsp;&#8199;</div>`;
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([
    { label: 'Tecken', value: String(preheader.length) },
    { label: 'UTF-8 bytes', value: String(utf8ByteLength(preheader)) },
    { label: 'Förhandsvisning', value: preheader.slice(0, 90) }
  ])}${codeBlock('Dold preheader-HTML', snippet)}`;
}

async function handleUtmBuilder(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  let url: URL;
  try {
    url = new URL(values.url);
  } catch {
    url = new URL(`https://${values.url}`);
  }
  const params: Record<string, string> = {
    utm_source: values.source,
    utm_medium: values.medium,
    utm_campaign: values.campaign,
    utm_term: values.term,
    utm_content: values.content
  };
  Object.entries(params).forEach(([key, value]) => {
    if (value?.trim()) {
      url.searchParams.set(key, value.trim());
    }
  });
  result.innerHTML = `${codeBlock('UTM-länk', url.toString())}${renderKeyValues(Object.entries(params).filter(([, value]) => value?.trim()).map(([label, value]) => ({ label, value })))}`;
}

async function handleJsonFormatter(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const parsed = JSON.parse(values.json);
  const output = values.mode === 'minify' ? JSON.stringify(parsed) : JSON.stringify(parsed, null, 2);
  const summary = Array.isArray(parsed)
    ? `${parsed.length} objekt i array`
    : parsed && typeof parsed === 'object'
      ? `${Object.keys(parsed).length} toppnivåfält`
      : typeof parsed;
  result.innerHTML = `${renderIssues([])}${renderKeyValues([{ label: 'Typ', value: summary }])}${codeBlock(values.mode === 'minify' ? 'Minifierad JSON' : 'Formaterad JSON', output)}`;
}

function decodeBase64UrlJson(value: string): unknown {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  return JSON.parse(decodeBase64Text(padded));
}

function unixClaim(value: unknown): string {
  return typeof value === 'number' ? new Date(value * 1000).toLocaleString('sv-SE') : '-';
}

async function handleJwtDecoder(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const parts = values.token.trim().split('.');
  if (parts.length < 2) {
    throw new Error('Token måste minst ha header och payload separerade med punkt.');
  }
  const header = decodeBase64UrlJson(parts[0]) as Record<string, unknown>;
  const payload = decodeBase64UrlJson(parts[1]) as Record<string, unknown>;
  result.innerHTML = `${renderIssues([{ severity: 'info', message: 'Signaturen visas inte som verifierad. Detta är endast en decoder.' }])}${renderKeyValues([
    { label: 'Algoritm', value: String(header.alg ?? '-') },
    { label: 'Typ', value: String(header.typ ?? '-') },
    { label: 'Issued at', value: unixClaim(payload.iat) },
    { label: 'Expires', value: unixClaim(payload.exp) }
  ])}${codeBlock('Header', JSON.stringify(header, null, 2))}${codeBlock('Payload', JSON.stringify(payload, null, 2))}`;
}

function randomBytes(count: number): Uint8Array {
  const bytes = new Uint8Array(count);
  crypto.getRandomValues(bytes);
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function handleUuidGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const count = Math.max(1, Math.min(100, Number(values.count) || 1));
  const uuids = Array.from({ length: count }, () => crypto.randomUUID());
  result.innerHTML = codeBlock('UUID v4', uuids.join('\n'));
}

async function handleApiKeyGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const count = Math.max(1, Math.min(20, Number(values.count) || 1));
  const byteCount = Math.max(16, Math.min(64, Number(values.bytes) || 32));
  const keys = Array.from({ length: count }, () => {
    const bytes = randomBytes(byteCount);
    const body = values.format === 'hex' ? bytesToHex(bytes) : bytesToBase64Url(bytes);
    return values.prefix?.trim() ? `${values.prefix.trim()}_${body}` : body;
  });
  result.innerHTML = `${renderKeyValues([
    { label: 'Bytes slumpdata', value: String(byteCount) },
    { label: 'Format', value: values.format }
  ])}${codeBlock('API-nycklar', keys.join('\n'))}`;
}

function emailValidation(address: string): { address: string; status: string; details: string } {
  const trimmed = address.trim();
  const basicPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [local = '', domain = ''] = trimmed.split('@');
  const issues: string[] = [];
  if (!basicPattern.test(trimmed)) {
    issues.push('Ogiltigt format');
  }
  if (trimmed.length > 254) {
    issues.push('Adressen är längre än 254 tecken');
  }
  if (local.length > 64) {
    issues.push('Lokaldelen är längre än 64 tecken');
  }
  if (domain.includes('..') || local.includes('..')) {
    issues.push('Innehåller dubbla punkter');
  }
  if (/^[.-]|[.-]$/.test(local) || /^[.-]|[.-]$/.test(domain)) {
    issues.push('Börjar eller slutar med punkt/bindestreck');
  }
  if (domain.toLowerCase() === 'example.com' || domain.toLowerCase().endsWith('.test')) {
    issues.push('Testdomän');
  }
  return {
    address: trimmed,
    status: issues.some((issue) => issue.startsWith('Ogiltigt')) ? 'Fel' : issues.length > 0 ? 'Varning' : 'OK',
    details: issues.length > 0 ? issues.join(', ') : 'Ser rimlig ut'
  };
}

async function handleEmailValidator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const rows = lines(values.addresses).map(emailValidation);
  const html = rows.map((row) => `<tr><td>${escapeHtml(row.address)}</td><td>${escapeHtml(row.status)}</td><td>${escapeHtml(row.details)}</td></tr>`).join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Adress</th><th>Status</th><th>Detaljer</th></tr></thead><tbody>${html}</tbody></table></div>`;
}

function slugTag(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'test';
}

async function handlePlusAddressGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const [local, domain] = values.email.split('@');
  if (!local || !domain) {
    throw new Error('Skriv en giltig basadress.');
  }
  const addresses = lines(values.tags).map((tag) => `${local}+${slugTag(tag)}@${domain}`);
  result.innerHTML = codeBlock('Plus-adresser', addresses.join('\n'));
}

function randomFromCharset(length: number, charset: string): string {
  if (!charset) {
    throw new Error('Välj minst en teckentyp.');
  }
  const bytes = randomBytes(length);
  return Array.from(bytes).map((byte) => charset[byte % charset.length]).join('');
}

async function handlePasswordGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const length = Math.max(8, Math.min(128, Number(values.length) || 24));
  const count = Math.max(1, Math.min(20, Number(values.count) || 5));
  const charsets = [
    values.lowercase ? 'abcdefghijklmnopqrstuvwxyz' : '',
    values.uppercase ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' : '',
    values.numbers ? '0123456789' : '',
    values.symbols ? '!@#$%^&*()-_=+[]{};:,.?/' : ''
  ];
  const charset = charsets.join('');
  const passwords = Array.from({ length: count }, () => randomFromCharset(length, charset));
  result.innerHTML = `${renderKeyValues([{ label: 'Längd', value: String(length) }, { label: 'Antal', value: String(count) }])}${codeBlock('Lösenord', passwords.join('\n'))}`;
}

async function handleTestEmailGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const count = Math.max(1, Math.min(100, Number(values.count) || 10));
  const domain = normalizeHost(values.domain || 'example.com');
  const prefix = slugTag(values.prefix || 'test');
  const addresses = Array.from({ length: count }, () => `${prefix}-${bytesToHex(randomBytes(4))}@${domain}`);
  result.innerHTML = codeBlock('Testadresser', addresses.join('\n'));
}

function htmlToText(html: string): string {
  const document = new DOMParser().parseFromString(html, 'text/html');
  document.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
  document.querySelectorAll('p, div, h1, h2, h3, li').forEach((node) => node.append('\n'));
  document.querySelectorAll('a[href]').forEach((node) => {
    const href = node.getAttribute('href') ?? '';
    if (href && !node.textContent?.includes(href)) {
      node.append(` (${href})`);
    }
  });
  return (document.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
}

async function handleHtmlToText(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const output = htmlToText(values.html);
  result.innerHTML = `${renderKeyValues([{ label: 'Tecken', value: String(output.length) }, { label: 'Bytes', value: String(utf8ByteLength(output)) }])}${codeBlock('Plain text', output)}`;
}

function textToHtml(text: string): string {
  return text.trim().split(/\n{2,}/).map((paragraph) => {
    const escaped = escapeHtml(paragraph).replace(/\n/g, '<br>');
    const linked = escaped.replace(/https?:\/\/[^\s<]+/g, (url) => `<a href="${url}">${url}</a>`);
    return `<p>${linked}</p>`;
  }).join('\n');
}

async function handleTextToHtml(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const output = textToHtml(values.text);
  result.innerHTML = `${renderKeyValues([{ label: 'HTML bytes', value: String(utf8ByteLength(output)) }])}${codeBlock('HTML', output)}`;
}

function extractLinks(content: string): string[] {
  const html = new DOMParser().parseFromString(content, 'text/html');
  const hrefs = Array.from(html.querySelectorAll<HTMLAnchorElement>('a[href]')).map((link) => link.href || link.getAttribute('href') || '');
  const rawUrls = content.match(/https?:\/\/[^\s"'<>]+/g) ?? [];
  return Array.from(new Set([...hrefs, ...rawUrls].map((url) => url.trim()).filter(Boolean)));
}

async function handleLinkExtractor(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const links = extractLinks(values.content);
  if (links.length === 0) {
    result.innerHTML = '<p class="tool-empty">Inga länkar hittades.</p>';
    return;
  }
  const rows = links.map((link) => {
    try {
      const url = new URL(link);
      const hasUtm = Array.from(url.searchParams.keys()).some((key) => key.startsWith('utm_'));
      return `<tr><td><code>${escapeHtml(link)}</code></td><td>${escapeHtml(url.hostname)}</td><td>${escapeHtml(url.protocol.replace(':', ''))}</td><td>${hasUtm ? 'Ja' : 'Nej'}</td></tr>`;
    } catch {
      return `<tr><td><code>${escapeHtml(link)}</code></td><td>-</td><td>-</td><td>-</td></tr>`;
    }
  }).join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Länk</th><th>Domän</th><th>Protokoll</th><th>UTM</th></tr></thead><tbody>${rows}</tbody></table></div>${codeBlock('Alla länkar', links.join('\n'))}`;
}

async function handleEmailSize(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const htmlBytes = utf8ByteLength(values.html ?? '');
  const textBytes = utf8ByteLength(values.text ?? '');
  const attachmentBytes = Math.max(0, Number(values.attachmentsKb) || 0) * 1024;
  const mimeOverhead = Math.round((htmlBytes + textBytes + attachmentBytes) * 0.08);
  const total = htmlBytes + textBytes + attachmentBytes + mimeOverhead;
  const issues: Issue[] = [];
  if (htmlBytes > 102 * 1024) {
    issues.push({ severity: 'warning', message: 'HTML-delen är över 102 KB och kan klippas i Gmail.' });
  }
  if (total > 1024 * 1024) {
    issues.push({ severity: 'warning', message: 'Mejlet är över 1 MB. Överväg att minska innehåll eller bilagor.' });
  }
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([
    { label: 'HTML', value: `${htmlBytes} bytes (${(htmlBytes / 1024).toFixed(1)} KB)` },
    { label: 'Plain text', value: `${textBytes} bytes (${(textBytes / 1024).toFixed(1)} KB)` },
    { label: 'Bilagor', value: `${attachmentBytes} bytes (${(attachmentBytes / 1024).toFixed(1)} KB)` },
    { label: 'Beräknat MIME-overhead', value: `${mimeOverhead} bytes` },
    { label: 'Totalt', value: `${total} bytes (${(total / 1024).toFixed(1)} KB)` }
  ])}`;
}

async function dnsLookup(domain: string, type: string): Promise<DnsRecord[]> {
  const response = await api<{ records: DnsRecord[]; issues: Issue[]; fromCache: boolean }>(`/api/verktyg/dns/uppslagning?domain=${encodeURIComponent(normalizeHost(domain))}&type=${encodeURIComponent(type)}`);
  return response.records;
}

async function handlePlainTextEmail(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const parts = [
    values.greeting?.trim(),
    values.body?.trim(),
    values.ctaText?.trim() && values.ctaUrl?.trim() ? `${values.ctaText.trim()}: ${values.ctaUrl.trim()}` : '',
    values.signature?.trim() ? `\n${values.signature.trim()}` : ''
  ].filter(Boolean);
  const output = parts.join('\n\n');
  result.innerHTML = `${renderKeyValues([{ label: 'Tecken', value: String(output.length) }, { label: 'Bytes', value: String(utf8ByteLength(output)) }])}${codeBlock('Plain text-mejl', output)}`;
}

async function handleHtmlEmailValidator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const document = new DOMParser().parseFromString(values.html, 'text/html');
  const issues: Issue[] = [];
  if (document.querySelectorAll('script').length > 0) {
    issues.push({ severity: 'error', message: 'Script-taggar fungerar inte i e-post och bör tas bort.' });
  }
  if (document.querySelectorAll('form, input, button, select, textarea').length > 0) {
    issues.push({ severity: 'warning', message: 'Formulär och interaktiva fält stöds dåligt i e-postklienter.' });
  }
  const images = Array.from(document.querySelectorAll<HTMLImageElement>('img'));
  const missingAlt = images.filter((image) => !image.alt).length;
  if (missingAlt > 0) {
    issues.push({ severity: 'warning', message: `${missingAlt} bild(er) saknar alt-text.` });
  }
  const httpLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]')).filter((link) => link.href.startsWith('http:')).length;
  if (httpLinks > 0) {
    issues.push({ severity: 'warning', message: `${httpLinks} länk(ar) använder HTTP i stället för HTTPS.` });
  }
  const htmlBytes = utf8ByteLength(values.html);
  if (htmlBytes > 102 * 1024) {
    issues.push({ severity: 'warning', message: 'HTML är över 102 KB och kan klippas i Gmail.' });
  }
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([
    { label: 'Bilder', value: String(images.length) },
    { label: 'Länkar', value: String(document.querySelectorAll('a[href]').length) },
    { label: 'HTML bytes', value: String(htmlBytes) }
  ])}`;
}

async function handleHtmlEmailPreview(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const links = extractLinks(values.html);
  result.innerHTML = `${renderKeyValues([{ label: 'Länkar', value: String(links.length) }, { label: 'HTML bytes', value: String(utf8ByteLength(values.html)) }])}<iframe sandbox srcdoc="${escapeHtml(values.html)}" style="width:100%;height:420px;border:1px solid rgba(238,245,241,.14);border-radius:8px;background:white;"></iframe>`;
}

function markdownInline(value: string): string {
  return escapeHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/https?:\/\/[^\s<]+/g, (url) => `<a href="${url}">${url}</a>`);
}

async function handleMarkdownEmail(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const html = values.markdown.split(/\n{2,}/).map((block) => {
    const trimmed = block.trim();
    if (trimmed.startsWith('### ')) {
      return `<h3>${markdownInline(trimmed.slice(4))}</h3>`;
    }
    if (trimmed.startsWith('## ')) {
      return `<h2>${markdownInline(trimmed.slice(3))}</h2>`;
    }
    if (trimmed.startsWith('# ')) {
      return `<h1>${markdownInline(trimmed.slice(2))}</h1>`;
    }
    if (/^[-*] /m.test(trimmed)) {
      const items = trimmed.split('\n').filter((line) => /^[-*] /.test(line)).map((line) => `<li>${markdownInline(line.slice(2))}</li>`).join('');
      return `<ul>${items}</ul>`;
    }
    return `<p>${markdownInline(trimmed).replace(/\n/g, '<br>')}</p>`;
  }).join('\n');
  result.innerHTML = `${renderKeyValues([{ label: 'HTML bytes', value: String(utf8ByteLength(html)) }])}${codeBlock('HTML', html)}`;
}

async function handleEmailPreview(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const inbox = `<div style="font-family:Arial,sans-serif;padding:12px;border-bottom:1px solid #ddd;"><strong>${escapeHtml(values.subject)}</strong><br><span style="color:#666;">${escapeHtml(values.preheader)}</span></div>`;
  const desktop = `<iframe sandbox srcdoc="${escapeHtml(values.html)}" style="width:100%;height:320px;border:1px solid rgba(238,245,241,.14);background:white;"></iframe>`;
  const mobile = `<iframe sandbox srcdoc="${escapeHtml(values.html)}" style="width:375px;max-width:100%;height:320px;border:1px solid rgba(238,245,241,.14);background:white;"></iframe>`;
  result.innerHTML = `${renderKeyValues([{ label: 'Ämnesrad', value: `${values.subject.length} tecken` }, { label: 'Preheader', value: `${values.preheader.length} tecken` }])}<div class="tool-code-block"><div><strong>Inkorgsrad</strong></div><pre>${escapeHtml(inbox)}</pre></div><h2>Desktop</h2>${desktop}<h2>Mobil</h2>${mobile}`;
}

async function handleOpenRedirectChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const redirectKeys = ['url', 'u', 'redirect', 'redirect_url', 'return', 'returnUrl', 'next', 'target', 'to', 'continue'];
  const rows = lines(values.urls).map((value) => {
    try {
      const url = new URL(value);
      const hits = redirectKeys.flatMap((key) => {
        const target = url.searchParams.get(key);
        if (!target) {
          return [];
        }
        try {
          const targetUrl = new URL(target, url.origin);
          const external = targetUrl.hostname !== url.hostname;
          return [`${key}=${targetUrl.toString()} ${external ? '(extern)' : '(intern)'}`];
        } catch {
          return [`${key}=${target}`];
        }
      });
      return `<tr><td><code>${escapeHtml(value)}</code></td><td>${escapeHtml(hits.join('\n') || 'Inga redirect-parametrar')}</td></tr>`;
    } catch {
      return `<tr><td><code>${escapeHtml(value)}</code></td><td>Ogiltig URL</td></tr>`;
    }
  }).join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>URL</th><th>Analys</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

async function handleBrokenLinkChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const document = new DOMParser().parseFromString(values.content, 'text/html');
  const htmlLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('a')).map((link) => link.getAttribute('href') ?? '');
  const rawLinks = values.content.match(/https?:\/\/[^\s"'<>]+/g) ?? [];
  const links = Array.from(new Set([...htmlLinks, ...rawLinks]));
  const rows = links.map((link) => {
    const issues: string[] = [];
    if (!link || link === '#') issues.push('Tom/placeholder');
    if (link.startsWith('http:')) issues.push('HTTP');
    if (/\{\{|\[|\]/.test(link)) issues.push('Mallvariabel eller placeholder');
    if (!/^(https?:|mailto:|tel:)/.test(link) && link) issues.push('Ovanligt protokoll eller relativ länk');
    return `<tr><td><code>${escapeHtml(link || '(tom)')}</code></td><td>${escapeHtml(issues.join(', ') || 'OK')}</td></tr>`;
  }).join('');
  result.innerHTML = links.length === 0 ? '<p class="tool-empty">Inga länkar hittades.</p>' : `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Länk</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

const disposableDomains = new Set(['mailinator.com', '10minutemail.com', 'guerrillamail.com', 'tempmail.com', 'temp-mail.org', 'yopmail.com', 'sharklasers.com', 'trashmail.com', 'getnada.com', 'example.com', 'example.net', 'example.org']);

async function handleDisposableDetector(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const rows = lines(values.addresses).map((address) => {
    const domain = address.split('@')[1]?.toLowerCase() ?? '';
    const disposable = disposableDomains.has(domain) || domain.endsWith('.test');
    return `<tr><td>${escapeHtml(address)}</td><td>${disposable ? 'Flaggad' : 'Inte flaggad'}</td><td>${escapeHtml(domain || '-')}</td></tr>`;
  }).join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Adress</th><th>Status</th><th>Domän</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

async function hmacHex(secret: string, payload: string, algorithm: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: algorithm }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(payload));
  return bytesToHex(new Uint8Array(signature));
}

async function handleHmacGenerator(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const signature = await hmacHex(values.secret, values.payload, values.algorithm);
  result.innerHTML = `${renderKeyValues([{ label: 'Algoritm', value: values.algorithm }])}${codeBlock('HMAC hex', signature)}`;
}

async function handleWebhookTester(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const payload = values.payload?.trim() || JSON.stringify({ event: values.eventName, id: crypto.randomUUID(), createdAt: new Date().toISOString() }, null, 2);
  const signature = values.secret?.trim() ? await hmacHex(values.secret, payload, 'SHA-256') : '';
  const headers = [`X-Webhook-Event: ${values.eventName}`, signature ? `X-Webhook-Signature: sha256=${signature}` : ''].filter(Boolean).join('\n');
  result.innerHTML = `${codeBlock('Payload', payload)}${codeBlock('Headers', headers || 'Inga signaturheaders')}`;
}

async function handleSpamScore(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const content = `${values.subject} ${htmlToText(values.html ?? '')} ${values.text ?? ''}`.toLowerCase();
  const triggers = ['gratis', 'free', 'vinn', 'winner', 'rabatt', 'cash', 'bonus', 'klicka nu', 'urgent', 'riskfri', '100%'];
  let score = 0;
  const issues: Issue[] = [];
  triggers.forEach((word) => {
    if (content.includes(word)) {
      score += 1;
      issues.push({ severity: 'warning', message: `Triggerord hittat: ${word}` });
    }
  });
  if ((values.subject ?? '').length > 70) {
    score += 1;
    issues.push({ severity: 'warning', message: 'Ämnesraden är lång.' });
  }
  if ((content.match(/https?:\/\//g) ?? []).length > 8) {
    score += 1;
    issues.push({ severity: 'warning', message: 'Många länkar kan öka risken.' });
  }
  if (/[A-ZÅÄÖ]{8,}/.test(values.subject ?? '')) {
    score += 1;
    issues.push({ severity: 'warning', message: 'Ämnesraden innehåller långa versalsekvenser.' });
  }
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([{ label: 'Ungefärlig score', value: String(score) }, { label: 'Risknivå', value: score >= 4 ? 'Hög' : score >= 2 ? 'Medel' : 'Låg' }])}`;
}

async function handleSpfChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const record = await lookupSpfRecord(values.domain);
  const validation = validateSpfRecord(record);
  result.innerHTML = `${renderIssues(validation.issues)}${codeBlock('SPF TXT', record)}${renderKeyValues(validation.terms)}<div class="tool-metrics"><span>${validation.lookupCount}/10 DNS-uppslag</span></div>`;
}

async function handleDomainHealth(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const domain = normalizeHost(values.domain);
  const [mx, txt, dmarc, a, aaaa, ns] = await Promise.all([
    dnsLookup(domain, 'MX'), dnsLookup(domain, 'TXT'), dnsLookup(`_dmarc.${domain}`, 'TXT'), dnsLookup(domain, 'A'), dnsLookup(domain, 'AAAA'), dnsLookup(domain, 'NS')
  ]);
  const spf = txt.filter((record) => record.value.toLowerCase().startsWith('v=spf1'));
  const dmarcRecords = dmarc.filter((record) => record.value.toLowerCase().startsWith('v=dmarc1'));
  const checks = [
    { name: 'MX', status: mx.length > 0 ? 'pass' : 'warning', details: `${mx.length} post(er)` },
    { name: 'SPF', status: spf.length === 1 ? 'pass' : 'warning', details: `${spf.length} SPF-post(er)` },
    { name: 'DMARC', status: dmarcRecords.length === 1 ? 'pass' : 'warning', details: `${dmarcRecords.length} DMARC-post(er)` },
    { name: 'A/AAAA', status: a.length + aaaa.length > 0 ? 'pass' : 'warning', details: `${a.length} A, ${aaaa.length} AAAA` },
    { name: 'NS', status: ns.length >= 2 ? 'pass' : 'warning', details: `${ns.length} namnserver/poster` }
  ];
  const html = checks.map((check) => `<div class="tool-check-result tool-check-result--${check.status}"><strong>${escapeHtml(check.name)}</strong><span>${escapeHtml(check.details)}</span></div>`).join('');
  result.innerHTML = `<div class="tool-check-grid">${html}</div>${renderRecords([...mx, ...spf, ...dmarcRecords, ...a, ...aaaa, ...ns])}`;
}

async function handleWildcardDns(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const randomHost = `${bytesToHex(randomBytes(4))}.${domain}`;
  const [a, aaaa] = await Promise.all([dnsLookup(randomHost, 'A'), dnsLookup(randomHost, 'AAAA')]);
  const hasWildcard = a.length + aaaa.length > 0;
  result.innerHTML = `${renderIssues([{ severity: hasWildcard ? 'warning' : 'info', message: hasWildcard ? 'Slumpad subdomän fick DNS-svar. Wildcard DNS verkar finnas.' : 'Ingen wildcard DNS hittades för den slumpade subdomänen.' }])}${renderKeyValues([{ label: 'Testat hostnamn', value: randomHost }, { label: 'A/AAAA-svar', value: String(a.length + aaaa.length) }])}${renderRecords([...a, ...aaaa])}`;
}

async function handleIpv6Readiness(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const [domainAaaa, mx] = await Promise.all([dnsLookup(domain, 'AAAA'), dnsLookup(domain, 'MX')]);
  const mxAaaaResults = await Promise.all(mx.map(async (record) => ({ host: record.value, records: await dnsLookup(record.value, 'AAAA') })));
  const rows = [
    `<tr><td>${escapeHtml(domain)}</td><td>Domän</td><td>${domainAaaa.length}</td></tr>`,
    ...mxAaaaResults.map((item) => `<tr><td>${escapeHtml(item.host)}</td><td>MX</td><td>${item.records.length}</td></tr>`)
  ].join('');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Host</th><th>Typ</th><th>AAAA-poster</th></tr></thead><tbody>${rows}</tbody></table></div>${renderRecords([...domainAaaa, ...mxAaaaResults.flatMap((item) => item.records)])}`;
}

async function handleNameserverComparison(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const [ns, mx, txt, dmarc] = await Promise.all([dnsLookup(domain, 'NS'), dnsLookup(domain, 'MX'), dnsLookup(domain, 'TXT'), dnsLookup(`_dmarc.${domain}`, 'TXT')]);
  const spf = txt.filter((record) => record.value.toLowerCase().startsWith('v=spf1'));
  result.innerHTML = `${renderKeyValues([{ label: 'Namnservrar', value: String(ns.length) }, { label: 'MX', value: String(mx.length) }, { label: 'SPF', value: String(spf.length) }, { label: 'DMARC', value: String(dmarc.length) }])}${renderRecords([...ns, ...mx, ...spf, ...dmarc])}`;
}

async function handleWhoisLookup(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const response = await api<{ domain: string; registrar?: string | null; registryDomainId?: string | null; statuses: string[]; nameservers: string[]; dates: ToolKeyValue[]; issues: Issue[] }>(`/api/verktyg/doman/whois?domain=${encodeURIComponent(domain)}`);
  const summary = [
    { label: 'Domän', value: response.domain },
    { label: 'Registrar', value: response.registrar || '-' },
    { label: 'Registry ID', value: response.registryDomainId || '-' },
    { label: 'Status', value: response.statuses.length ? response.statuses.join(', ') : '-' },
    { label: 'Namnservrar', value: response.nameservers.length ? response.nameservers.join(', ') : '-' }
  ];
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues(summary)}${response.dates.length ? renderKeyValues(response.dates) : ''}`;
}

async function handleDnsPropagation(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const domain = normalizeHost(values.domain);
  const response = await api<{ domain: string; type: string; results: { resolver: string; records: DnsRecord[]; status: string }[]; issues: Issue[] }>(`/api/verktyg/doman/spridning?domain=${encodeURIComponent(domain)}&type=${encodeURIComponent(values.type)}`);
  const rows = response.results.map((item) => {
    const values = item.records.length ? item.records.map((record) => `${record.priority ?? ''} ${record.value}`.trim()).join('<br>') : '-';
    const ttl = item.records.length ? item.records.map((record) => `${record.ttl}s`).join('<br>') : '-';
    return `<tr><td>${escapeHtml(item.resolver)}</td><td>${escapeHtml(item.status)}</td><td>${values}</td><td>${ttl}</td></tr>`;
  }).join('');
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues([{ label: 'Uppslag', value: `${response.domain} ${response.type}` }])}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Resolver</th><th>Status</th><th>Svar</th><th>TTL</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function renderBlacklistResults(results: BlacklistResult[]): string {
  const rows = results.map((item) => `<tr><td>${escapeHtml(item.listName)}</td><td>${item.listed ? 'Listad' : escapeHtml(item.status)}</td><td><code>${escapeHtml(item.query)}</code></td><td>${item.responses.length ? item.responses.map(escapeHtml).join('<br>') : '-'}</td></tr>`).join('');
  return `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Lista</th><th>Status</th><th>DNSBL-query</th><th>Svar</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

async function handleBlacklistLookup(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ ipAddress: string; results: BlacklistResult[]; issues: Issue[] }>(`/api/verktyg/doman/svartlista?ipAddress=${encodeURIComponent(values.ipAddress)}`);
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues([{ label: 'IP-adress', value: response.ipAddress }, { label: 'Listade träffar', value: String(response.results.filter((item) => item.listed).length) }])}${renderBlacklistResults(response.results)}`;
}

async function handleIpReputation(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ ipAddress: string; riskLevel: string; signals: ToolKeyValue[]; blacklists: BlacklistResult[]; issues: Issue[] }>(`/api/verktyg/doman/ip-rykte?ipAddress=${encodeURIComponent(values.ipAddress)}`);
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues([{ label: 'IP-adress', value: response.ipAddress }, { label: 'Risknivå', value: response.riskLevel }, ...response.signals])}${renderBlacklistResults(response.blacklists)}`;
}

async function handleDnssecChecker(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const [ds, dnskey] = await Promise.all([dnsLookup(domain, 'DS'), dnsLookup(domain, 'DNSKEY')]);
  const issues: Issue[] = [];
  if (ds.length === 0) {
    issues.push({ severity: 'warning', message: 'Ingen DS-post hittades. Föräldrazonen visar ingen DNSSEC-delegation i detta uppslag.' });
  }
  if (dnskey.length === 0) {
    issues.push({ severity: 'warning', message: 'Ingen DNSKEY-post hittades för domänen.' });
  }
  if (issues.length === 0) {
    issues.push({ severity: 'info', message: 'DS och DNSKEY hittades. Domänen verkar ha DNSSEC-signaler publicerade.' });
  }
  result.innerHTML = `${renderIssues(issues)}${renderKeyValues([{ label: 'DS-poster', value: String(ds.length) }, { label: 'DNSKEY-poster', value: String(dnskey.length) }])}${renderRecords([...ds, ...dnskey])}`;
}

function parseCaaRecord(record: DnsRecord): { flags: string; tag: string; value: string } {
  const match = record.value.match(/^(\d+)\s+([a-z0-9_-]+)\s+"?([^"]*)"?$/i);
  return {
    flags: match?.[1] ?? '-',
    tag: match?.[2] ?? 'okänd',
    value: match?.[3] ?? record.value
  };
}

async function handleCaaChecker(form: HTMLFormElement, result: Element) {
  const domain = normalizeHost(formValues(form).domain);
  const records = await dnsLookup(domain, 'CAA');
  const issues: Issue[] = records.length === 0
    ? [{ severity: 'warning', message: 'Ingen CAA-post hittades. Det är tillåtet, men då begränsas inte vilka CA:er som får utfärda certifikat.' }]
    : [{ severity: 'info', message: `${records.length} CAA-post(er) hittades.` }];
  const rows = records.map(parseCaaRecord).map((record) => `<tr><td>${escapeHtml(record.flags)}</td><td><code>${escapeHtml(record.tag)}</code></td><td>${escapeHtml(record.value)}</td></tr>`).join('');
  const table = rows ? `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Flags</th><th>Tagg</th><th>Värde</th></tr></thead><tbody>${rows}</tbody></table></div>` : '';
  result.innerHTML = `${renderIssues(issues)}${table}${renderRecords(records)}`;
}

async function handleTlsCertificateChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await api<{ host: string; port: number; subject: string; issuer: string; notBeforeUtc: string; notAfterUtc: string; subjectAlternativeNames: string[]; protocol: string; cipher: string; issues: Issue[]; success: boolean }>('/api/verktyg/smtp/tls-certifikat', {
    method: 'POST',
    body: JSON.stringify({ host: normalizeHost(values.host), port: Number(values.port) })
  });
  const date = (value: string) => value.startsWith('0001-') ? '-' : new Date(value).toLocaleString('sv-SE');
  const details = [
    { label: 'Host', value: `${response.host}:${response.port}` },
    { label: 'Subject', value: response.subject || '-' },
    { label: 'Issuer', value: response.issuer || '-' },
    { label: 'Giltigt från', value: date(response.notBeforeUtc) },
    { label: 'Giltigt till', value: date(response.notAfterUtc) },
    { label: 'Protokoll', value: response.protocol || '-' },
    { label: 'Cipher', value: response.cipher || '-' },
    { label: 'SAN-namn', value: response.subjectAlternativeNames.length ? response.subjectAlternativeNames.join(', ') : '-' }
  ];
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues(details)}`;
}

async function handleSmtpBannerChecker(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await runSmtpProbe(values, { useStartTls: false, useSsl: Boolean(values.useSsl) });
  const serverText = smtpServerText(response.steps);
  const features = (serverText.match(/\b(STARTTLS|AUTH|SIZE|PIPELINING|8BITMIME|SMTPUTF8|CHUNKING|DSN)\b/gi) ?? [])
    .map((item) => item.toUpperCase())
    .filter((item, index, all) => all.indexOf(item) === index);
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues([{ label: 'Status', value: response.success ? 'Svar mottaget' : 'Testet slutfördes inte' }, { label: 'Annonserade funktioner', value: features.length ? features.join(', ') : '-' }])}${renderSmtpLog(response.steps)}`;
}

async function handleSmtpCommandPlayground(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const serverName = values.serverName || 'mail.example.com';
  const transcript: { direction: string; message: string }[] = [{ direction: 'server', message: `220 ${serverName} ESMTP` }];
  let inData = false;
  for (const rawLine of (values.commands ?? '').replace(/\r\n/g, '\n').split('\n')) {
    const line = rawLine.trimEnd();
    if (!line && !inData) {
      continue;
    }
    transcript.push({ direction: 'client', message: line });
    const upper = line.toUpperCase();
    if (inData) {
      if (line === '.') {
        transcript.push({ direction: 'server', message: '250 2.0.0 Message accepted for delivery' });
        inData = false;
      }
      continue;
    }
    if (upper.startsWith('EHLO')) {
      transcript.push({ direction: 'server', message: `250-${serverName}\n250-STARTTLS\n250-AUTH PLAIN LOGIN\n250-SIZE 35882577\n250 SMTPUTF8` });
    } else if (upper.startsWith('HELO')) {
      transcript.push({ direction: 'server', message: `250 ${serverName}` });
    } else if (upper.startsWith('MAIL FROM:')) {
      transcript.push({ direction: 'server', message: '250 2.1.0 Sender OK' });
    } else if (upper.startsWith('RCPT TO:')) {
      transcript.push({ direction: 'server', message: '250 2.1.5 Recipient OK' });
    } else if (upper === 'DATA') {
      transcript.push({ direction: 'server', message: '354 End data with <CR><LF>.<CR><LF>' });
      inData = true;
    } else if (upper === 'RSET') {
      transcript.push({ direction: 'server', message: '250 2.0.0 Reset state' });
    } else if (upper === 'NOOP') {
      transcript.push({ direction: 'server', message: '250 2.0.0 OK' });
    } else if (upper === 'QUIT') {
      transcript.push({ direction: 'server', message: '221 2.0.0 Bye' });
    } else {
      transcript.push({ direction: 'server', message: '502 5.5.2 Command not recognized in this simulator' });
    }
  }
  const rows = transcript.map((step) => `<tr><td>${escapeHtml(step.direction)}</td><td><code>${escapeHtml(step.message)}</code></td></tr>`).join('');
  const text = transcript.map((step) => `${step.direction === 'client' ? 'C' : 'S'}: ${step.message}`).join('\n');
  result.innerHTML = `<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>Part</th><th>Meddelande</th></tr></thead><tbody>${rows}</tbody></table></div>${codeBlock('Transkript', text)}`;
}

async function handleSmtpRelayTest(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const response = await runSmtpProbe(values);
  const serverText = smtpServerText(response.steps);
  const findings: Issue[] = [
    { severity: 'info', message: 'Verktyget skickar inget mejl och gör ingen RCPT-probning. Det här är en försiktig riskanalys.' }
  ];
  if (/AUTH/i.test(serverText)) {
    findings.push({ severity: 'info', message: 'Servern annonserar AUTH, vilket normalt betyder att relaying ska kräva inloggning.' });
  } else {
    findings.push({ severity: 'warning', message: 'Servern annonserar inte AUTH i EHLO-svaret. Kontrollera relay-regler i serverkonfigurationen.' });
  }
  if (!/STARTTLS/i.test(serverText) && Number(values.port) !== 465) {
    findings.push({ severity: 'warning', message: 'STARTTLS annonserades inte. Publika SMTP-flöden bör normalt kunna använda TLS.' });
  }
  result.innerHTML = `${renderIssues([...response.issues, ...findings])}${renderKeyValues([{ label: 'Testläge', value: 'Ingen MAIL FROM, RCPT TO eller DATA skickades' }, { label: 'SMTP-status', value: response.success ? 'Svar mottaget' : 'Testet slutfördes inte' }])}${renderSmtpLog(response.steps)}`;
}

async function handleSmtpLatencyTest(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const started = performance.now();
  const response = await runSmtpProbe(values);
  const total = Math.round(performance.now() - started);
  const rows = response.steps.map((step, index, steps) => {
    const previous = index === 0 ? 0 : steps[index - 1].elapsedMilliseconds;
    return `<tr><td>${index + 1}</td><td>${escapeHtml(step.direction)}</td><td>${step.elapsedMilliseconds} ms</td><td>${Math.max(0, step.elapsedMilliseconds - previous)} ms</td><td><code>${escapeHtml(step.message)}</code></td></tr>`;
  }).join('');
  result.innerHTML = `${renderIssues(response.issues)}${renderKeyValues([{ label: 'Total tid i webbläsaren', value: `${total} ms` }, { label: 'Servermätt SMTP-tid', value: `${response.steps.at(-1)?.elapsedMilliseconds ?? 0} ms` }, { label: 'Status', value: response.success ? 'Slutfört' : 'Ej slutfört' }])}<div class="tool-table-wrap"><table class="tool-table"><thead><tr><th>#</th><th>Part</th><th>Tid</th><th>Delta</th><th>Meddelande</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

function renderMjmlNode(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent ?? '');
  }
  if (!(node instanceof Element)) {
    return '';
  }
  const children = Array.from(node.childNodes).map(renderMjmlNode).join('');
  const tag = node.tagName.toLowerCase();
  const attr = (name: string, fallback = '') => node.getAttribute(name) ?? fallback;
  if (tag === 'mj-section') {
    return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:${escapeHtml(attr('background-color', '#ffffff'))};"><tr><td align="center"><table role="presentation" width="600" style="width:100%;max-width:600px;" cellspacing="0" cellpadding="0"><tr>${children}</tr></table></td></tr></table>`;
  }
  if (tag === 'mj-column') {
    return `<td style="padding:${escapeHtml(attr('padding', '16px'))};vertical-align:top;">${children}</td>`;
  }
  if (tag === 'mj-text') {
    return `<div style="font-family:Arial,sans-serif;font-size:${escapeHtml(attr('font-size', '16px'))};line-height:1.5;color:${escapeHtml(attr('color', '#1f2937'))};text-align:${escapeHtml(attr('align', 'left'))};">${children}</div>`;
  }
  if (tag === 'mj-button') {
    return `<div style="padding:16px 0;text-align:${escapeHtml(attr('align', 'left'))};"><a href="${escapeHtml(attr('href', '#'))}" style="display:inline-block;background:${escapeHtml(attr('background-color', '#0f766e'))};color:${escapeHtml(attr('color', '#ffffff'))};padding:12px 18px;text-decoration:none;border-radius:6px;font-family:Arial,sans-serif;">${children}</a></div>`;
  }
  if (tag === 'mj-image') {
    return `<img src="${escapeHtml(attr('src'))}" alt="${escapeHtml(attr('alt'))}" width="${escapeHtml(attr('width', '600'))}" style="display:block;max-width:100%;height:auto;border:0;" />`;
  }
  return children;
}

async function handleMjmlPreview(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const doc = new DOMParser().parseFromString(`<mjml>${values.mjml}</mjml>`, 'text/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('Markupen kunde inte tolkas. Kontrollera att taggarna är stängda.');
  }
  const body = Array.from(doc.documentElement.childNodes).map(renderMjmlNode).join('');
  const html = `<!doctype html><html><body style="margin:0;background:#f3f4f6;padding:24px;">${body}</body></html>`;
  result.innerHTML = `${renderKeyValues([{ label: 'Tecken i HTML', value: String(html.length) }, { label: 'Läge', value: 'Förenklad MJML-preview' }])}<iframe sandbox srcdoc="${escapeHtml(html)}" style="width:100%;height:420px;border:1px solid rgba(238,245,241,.14);border-radius:8px;background:white;"></iframe>${codeBlock('Genererad HTML', html)}`;
}

async function handleEmailApiPlayground(form: HTMLFormElement, result: Element) {
  const values = formValues(form);
  const payload = {
    from: values.from,
    to: [values.to],
    subject: values.subject,
    textBody: values.textBody || undefined,
    htmlBody: values.htmlBody || undefined
  };
  const json = JSON.stringify(payload, null, 2);
  const endpoint = values.endpoint || 'https://api.skickamejl.se/messages';
  const header = values.apiHeader || 'X-Api-Key';
  const curlJson = json.replace(/'/g, "'\"'\"'");
  const curl = `curl -X POST '${endpoint}' \\\n  -H 'Content-Type: application/json' \\\n  -H '${header}: YOUR_API_KEY' \\\n  --data '${curlJson}'`;
  const url = new URL(endpoint, window.location.origin);
  const http = `POST ${url.pathname}${url.search} HTTP/1.1\nHost: ${url.host}\nContent-Type: application/json\n${header}: YOUR_API_KEY\n\n${json}`;
  result.innerHTML = `${renderIssues([{ severity: 'info', message: 'Det här bygger bara request-exempel lokalt. Inget mejl skickas.' }])}${codeBlock('JSON payload', json)}${codeBlock('HTTP request', http)}${codeBlock('curl', curl)}`;
}

const handlers: Record<string, (form: HTMLFormElement, result: Element, submitter?: HTMLElement | null) => Promise<void>> = {
  spf: handleSpf,
  dkim: handleDkim,
  dmarc: handleDmarc,
  mx: handleMx,
  dns: handleDns,
  deliverability: handleDeliverability,
  smtp: handleSmtp,
  spfLookup: handleSpfLookup,
  txtLookup: handleTxtLookup,
  reverseDns: handleReverseDns,
  headerAnalyzer: handleHeaderAnalyzer,
  mimeDecoder: handleMimeDecoder,
  base64Codec: handleBase64Codec,
  urlCodec: handleUrlCodec,
  htmlEntityCodec: handleHtmlEntityCodec,
  spfFlattener: handleSpfFlattener,
  spfSyntax: handleSpfSyntax,
  spfCounter: handleSpfCounter,
  dmarcGenerator: handleDmarcGenerator,
  bimiGenerator: handleBimiGenerator,
  bimiChecker: handleBimiChecker,
  mtaStsGenerator: handleMtaStsGenerator,
  tlsRptGenerator: handleTlsRptGenerator,
  smtpResponseDecoder: handleSmtpResponseDecoder,
  subjectLength: handleSubjectLength,
  preheaderLength: handlePreheaderLength,
  utmBuilder: handleUtmBuilder,
  jsonFormatter: handleJsonFormatter,
  jwtDecoder: handleJwtDecoder,
  uuidGenerator: handleUuidGenerator,
  apiKeyGenerator: handleApiKeyGenerator,
  emailValidator: handleEmailValidator,
  plusAddressGenerator: handlePlusAddressGenerator,
  passwordGenerator: handlePasswordGenerator,
  testEmailGenerator: handleTestEmailGenerator,
  htmlToText: handleHtmlToText,
  textToHtml: handleTextToHtml,
  linkExtractor: handleLinkExtractor,
  emailSize: handleEmailSize,
  plainTextEmail: handlePlainTextEmail,
  htmlEmailValidator: handleHtmlEmailValidator,
  htmlEmailPreview: handleHtmlEmailPreview,
  markdownEmail: handleMarkdownEmail,
  emailPreview: handleEmailPreview,
  openRedirectChecker: handleOpenRedirectChecker,
  brokenLinkChecker: handleBrokenLinkChecker,
  disposableDetector: handleDisposableDetector,
  hmacGenerator: handleHmacGenerator,
  webhookTester: handleWebhookTester,
  spamScore: handleSpamScore,
  spfChecker: handleSpfChecker,
  domainHealth: handleDomainHealth,
  wildcardDns: handleWildcardDns,
  ipv6Readiness: handleIpv6Readiness,
  nameserverComparison: handleNameserverComparison,
  whoisLookup: handleWhoisLookup,
  dnsPropagation: handleDnsPropagation,
  blacklistLookup: handleBlacklistLookup,
  ipReputation: handleIpReputation,
  dnssecChecker: handleDnssecChecker,
  caaChecker: handleCaaChecker,
  tlsCertificateChecker: handleTlsCertificateChecker,
  smtpBannerChecker: handleSmtpBannerChecker,
  smtpCommandPlayground: handleSmtpCommandPlayground,
  smtpRelayTest: handleSmtpRelayTest,
  smtpLatencyTest: handleSmtpLatencyTest,
  mjmlPreview: handleMjmlPreview,
  emailApiPlayground: handleEmailApiPlayground
};

function bindForms() {
  $$<HTMLFormElement>('[data-tool-form]').forEach((form) => {
    const result = $('[data-tool-result]', form.closest('.tool-workspace') ?? document);
    if (!result) {
      return;
    }
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const tool = form.dataset.toolForm ?? '';
      const submitter = (event as SubmitEvent).submitter as HTMLElement | null;
      const handler = handlers[tool];
      if (!handler) {
        return;
      }
      setLoading(result);
      try {
        await handler(form, result, submitter);
      } catch (error) {
        setError(result, error);
      }
    });
  });
}

function bindCopyAndDownload() {
  document.addEventListener('click', async (event) => {
    const target = event.target as HTMLElement;
    if (target.matches('[data-copy]')) {
      const pre = target.closest('.tool-code-block')?.querySelector('pre');
      if (pre) {
        await navigator.clipboard.writeText(pre.textContent ?? '');
        target.textContent = 'Kopierat';
        window.setTimeout(() => { target.textContent = 'Copy'; }, 1400);
      }
    }
    if (target.matches('[data-download]')) {
      const pre = target.closest('.tool-code-block')?.querySelector('pre');
      if (!pre) {
        return;
      }
      const blob = new Blob([pre.textContent ?? ''], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pre.dataset.downloadName ?? 'download.txt';
      link.click();
      URL.revokeObjectURL(url);
    }
  });
}

function bindDeliverabilitySession() {
  const button = $('[data-generate-test-address]');
  const output = $('[data-test-address]');
  button?.addEventListener('click', async () => {
    if (!output) {
      return;
    }
    output.textContent = 'Skapar testadress...';
    try {
      const response = await api<{ token: string; testEmailAddress: string; expiresAtUtc: string }>('/api/verktyg/leveransbarhet/sessioner', { method: 'POST' });
      output.innerHTML = `<span>${escapeHtml(response.testEmailAddress)}</span><button type="button" data-copy-address>Kopiera</button><small>Giltig till ${new Date(response.expiresAtUtc).toLocaleString('sv-SE')}</small>`;
      output.querySelector('[data-copy-address]')?.addEventListener('click', async (event) => {
        await navigator.clipboard.writeText(response.testEmailAddress);
        const copyButton = event.currentTarget as HTMLButtonElement;
        copyButton.textContent = 'Kopierat';
        window.setTimeout(() => { copyButton.textContent = 'Kopiera'; }, 1400);
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte skapa testadress.';
      output.textContent = message === 'Något gick fel.' ? 'Logga in för att skapa en testadress.' : message;
    }
  });
}

function bindToolsSearch() {
  const input = $<HTMLInputElement>('[data-tools-search]');
  const cards = $$<HTMLElement>('[data-tool-card]');
  const sections = $$<HTMLElement>('[data-tools-section]');
  const count = $('[data-tools-search-count]');
  const empty = $<HTMLElement>('[data-tools-search-empty]');
  if (!input || cards.length === 0) {
    return;
  }

  const update = () => {
    const query = input.value.trim().toLowerCase();
    let visibleCount = 0;

    cards.forEach((card) => {
      const searchText = card.dataset.searchText ?? card.textContent?.toLowerCase() ?? '';
      const visible = query.length === 0 || searchText.includes(query);
      card.hidden = !visible;
      if (visible) {
        visibleCount += 1;
      }
    });

    sections.forEach((section) => {
      const hasVisibleCard = $$<HTMLElement>('[data-tool-card]', section).some((card) => !card.hidden);
      section.hidden = !hasVisibleCard;
    });

    if (count) {
      count.textContent = query.length === 0 ? `${cards.length} verktyg` : `${visibleCount} matchningar`;
    }
    if (empty) {
      empty.hidden = visibleCount > 0;
    }
  };

  input.addEventListener('input', update);
  update();
}

bindForms();
bindCopyAndDownload();
bindDeliverabilitySession();
bindToolsSearch();
